import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

export default function MobileGroupedCard({ group }) {
  const navigate = useNavigate();
  const [selectedVariant, setSelectedVariant] = useState(group.variants[0]);
  const [selectedStorage, setSelectedStorage] = useState(null);

  // Set default storage when component loads or variant changes
  useEffect(() => {
    if (selectedVariant?.availableStorage && selectedVariant.availableStorage.length > 0) {
      setSelectedStorage(selectedVariant.availableStorage[0]);
    }
  }, [selectedVariant]);

  // Helper function to map color names to hex codes
  const getColorHex = (colorName) => {
    const colorMap = {
      // Blacks & Grays
      'Black': '#000000',
      'Titanium Black': '#2C2C2E',
      'Titan Black': '#2C2C2E',
      'Titanium Jet Black': '#1C1C1C',
      'Graphite': '#3A3A3C',
      'Graphite Black': '#2C2C2C',
      'Awesome Graphite': '#3A3A3C',
      'Black Caviar': '#1C1C1C',
      'Deep Charcoal': '#36454F',
      'Dark Gray': '#4A4A4A',
      'Gray': '#808080',
      'Titanium Gray': '#6B7280',
      'Titan Gray': '#6B7280',
      'Lavender Gray': '#9CA3AF',
      'Awesome Lightgray': '#D1D5DB',
      'Blue Black': '#1E293B',

      // Whites & Silvers
      'White': '#FFFFFF',
      'Awesome White': '#FAFAFA',
      'Modern White': '#F8F8F8',
      'Silver': '#C0C0C0',
      'Titanium White Silver': '#E5E7EB',

      // Blues
      'Navy': '#1E3A8A',
      'Awesome Navy': '#1E3A8A',
      'Brushed Navy': '#1E40AF',
      'Titanium Silver Blue': '#60A5FA',
      'Light Blue': '#BFDBFE',
      'Iceblue': '#A5F3FC',
      'Awesome Iceblue': '#A5F3FC',

      // Purples & Violets
      'Violet': '#8B5CF6',
      'Awesome Violet': '#8B5CF6',
      'Light Violet': '#C4B5FD',
      'Lilac': '#C4B5FD',
      'Awesome Lilac': '#C4B5FD',
      'Bora Purple': '#A855F7',

      // Greens
      'Green': '#22C55E',
      'Mint': '#86EFAC',
      'Light Green': '#BBF7D0',
      'Titanium Jade Green': '#6EE7B7',
      'Lime': '#BEF264',
      'Awesome Lime': '#BEF264',
      'Awesome Olive': '#84CC16',

      // Yellows & Golds
      'Yellow': '#FCD34D',
      'Lemon': '#FEF08A',
      'Awesome Lemon': '#FEF08A',
      'Gold': '#F59E0B',
      'Pink Gold': '#E8B69A',
      'Titanium Pink Gold': '#E8B69A',
      'Sand Gold Metal': '#D4AF37',

      // Pinks
      'Peach Pink': '#FED7E2',
      'Awesome Pink': '#FCA5A5',

      // Browns & Beiges
      'Beige': '#F5E6D3',
      'Modern Brown': '#8B7355',
      'Modern Teak': '#B8956A',

      // Special Titanium Colors
      'Titanium': '#B4B4B4',
    };

    return colorMap[colorName] || '#CCCCCC';
  };

  // Use first color image if available
  const displayImage = selectedVariant.colorImages && selectedVariant.colorImages.length > 0
    ? selectedVariant.colorImages[0]
    : selectedVariant.image;

  return (
    <div className="product-card led-grouped-card">
      <div
        className="product-image"
        onClick={() => navigate(`/product/${selectedVariant.id}`)}
        style={{ cursor: 'pointer' }}
      >
        <img src={displayImage} alt={group.model} />
      </div>

      <h3
        onClick={() => navigate(`/product/${selectedVariant.id}`)}
        style={{ cursor: 'pointer' }}
      >
        {group.model}
      </h3>

      {/* Storage & RAM Selector - only show buttons if multiple variants */}
      {selectedVariant.availableStorage && selectedVariant.availableStorage.length > 1 && (
        <div className="led-size-selector mobile-storage-selector">
          <label>Storage & RAM:</label>
          <div className="size-options">
            {selectedVariant.availableStorage.map((storageInfo, index) => (
              <button
                key={index}
                className={`size-option ${selectedStorage?.storage === storageInfo.storage && selectedStorage?.ram === storageInfo.ram ? 'active' : ''}`}
                onClick={() => setSelectedStorage(storageInfo)}
              >
                {storageInfo.storage} / {storageInfo.ram}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Display storage and RAM - show for all products */}
      {selectedStorage ? (
        <div className="led-size-info mobile-storage-info">
          {selectedVariant.availableStorage && selectedVariant.availableStorage.length === 1 && (
            <div className="led-single-size">
              <span className="size-label">Storage & RAM:</span>
              <span className="size-value">{selectedStorage.storage} / {selectedStorage.ram}</span>
            </div>
          )}
        </div>
      ) : (
        /* Show storage/RAM for single variant products without availableStorage array */
        selectedVariant.storage && selectedVariant.ram && (
          <div className="led-size-info mobile-storage-info">
            <div className="led-single-size">
              <span className="size-label">Storage & RAM:</span>
              <span className="size-value">{selectedVariant.storage} / {selectedVariant.ram}</span>
            </div>
          </div>
        )
      )}

      {/* Color Dots */}
      {selectedVariant.colors && selectedVariant.colors.length > 0 && selectedVariant.colorImages && (
        <div className="product-card-colors">
          {selectedVariant.colors.slice(0, Math.min(selectedVariant.colorImages.length, 5)).map((color, index) => (
            <div
              key={index}
              className="color-dot"
              style={{
                backgroundColor: getColorHex(color),
                border: getColorHex(color) === '#FFFFFF' || getColorHex(color) === '#F8F8F8' ? '1.5px solid #ddd' : '1.5px solid rgba(0, 0, 0, 0.15)'
              }}
              title={color}
            />
          ))}
          {selectedVariant.colorImages.length > 5 && (
            <span style={{ fontSize: '0.75rem', color: '#666' }}>+{selectedVariant.colorImages.length - 5}</span>
          )}
        </div>
      )}

      <div className="product-price-section">
        <div className="price-info">
          <p className="current-price">PKR {(selectedStorage?.price || selectedVariant.price).toLocaleString()}</p>
          {(selectedStorage?.discount || selectedVariant.discount) > 0 && (
            <p className="original-price">PKR {(selectedStorage?.originalPrice || selectedVariant.originalPrice).toLocaleString()}</p>
          )}
        </div>
      </div>

      <button
        className="btn-add-to-cart"
        onClick={() => navigate(`/product/${selectedVariant.id}`)}
      >
        Shop Now
      </button>
    </div>
  );
}
